import React from 'react';
import { motion } from 'framer-motion';
import { Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStepManagement } from '../hooks/useStepManagement';
import { SEARCH_STEPS } from '../constants/searchSteps';
import Step from './Step';
import SearchButton from './SearchButton';
import ResultsPreview from './ResultsPreview';
import { BackgroundSection } from '@client/feature/activeSubstance/partials';

/**
 * Multi-step treatment search interface component
 * @param {Object} props - Component props
 * @param {Function} props.onSearch - Callback when search is triggered
 * @returns {JSX.Element} Multi-step search component
 */
const TreatmentSteps = ({ onSearch }) => {
  const { t } = useTranslation('treatment-search');

  const {
    currentStep,
    completedSteps,
    selections,
    selectionLabels,
    setCurrentStep,
    isStepValid,
    isStepRequired,
    areAllRequiredStepsValid,
    canAccessStep,
    getStepStatus,
    handleStepChange,
    handleStepSkip,
    handleStepClick,
    handleReset,
    forceStepRevalidation,
  } = useStepManagement();

  /**
   * Handles search execution with current selections
   */
  const handleSearch = () => {
    const searchData = {
      selections,
      selectionLabels,
      completedSteps: completedSteps.size,
    };
    onSearch(searchData);
  };

  return (
    <div className="relative w-full min-h-screen">
      <div className="container px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <BackgroundSection />
          <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
            <div className="filters-container">
              <div className="mb-6 sm:mb-8">
                <h3 className="text-2xl sm:text-3xl font-medium text-black mb-4 text-center capitalize px-4">
                  {t('search.title')}
                </h3>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <span className="text-sm text-gray-600">
                    {completedSteps.size}/{SEARCH_STEPS.length}
                  </span>
                  <Progress
                    percent={Math.round(
                      (completedSteps.size / SEARCH_STEPS.length) * 100
                    )}
                    size="small"
                    strokeColor="#5B8C00"
                    className="w-full max-w-48 sm:w-48"
                    showInfo={false}
                  />
                </div>
              </div>

              <div className="space-y-2 mb-6 sm:mb-8">
                {SEARCH_STEPS.map((step) => {
                  const status = getStepStatus(step.key);
                  const isActive = step.key === currentStep;
                  const canClick = canAccessStep(step.key);
                  const stepSelections = selections[step.field];
                  const stepLabels = selectionLabels[step.field] || [];
                  const isCompleted = completedSteps.has(step.key);
                  const isValid = isStepValid(step.key);
                  const isRequired = isStepRequired(step.key);

                  return (
                    <Step
                      key={step.key}
                      step={step}
                      status={status}
                      isActive={isActive}
                      canClick={canClick}
                      selections={stepSelections}
                      selectionLabels={stepLabels}
                      isCompleted={isCompleted}
                      isValid={isValid}
                      isRequired={isRequired}
                      onStepClick={handleStepClick}
                      onChange={handleStepChange}
                      onNext={setCurrentStep}
                      onSkip={handleStepSkip}
                      onForceRevalidation={forceStepRevalidation}
                    />
                  );
                })}
              </div>

              <SearchButton
                completedStepsCount={completedSteps.size}
                areAllRequiredStepsValid={areAllRequiredStepsValid()}
                onSearch={handleSearch}
                onReset={handleReset}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TreatmentSteps;
